import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, Check, X, ExternalLink, Mail, RotateCcw, Trash2 } from "lucide-react";
import { api } from "../lib/api";

const STATUSES = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "accepted", label: "Accepted" },
  { key: "rejected", label: "Rejected" },
];

const STATUS_STYLES = {
  pending: { bg: "#fff3e5", color: "#a35010", border: "#f0c39a" },
  accepted: { bg: "#eaf3ea", color: "#2d7a2d", border: "#b8d8b8" },
  rejected: { bg: "#fdecea", color: "#9b1c2a", border: "#f5a8b0" },
};

const CATEGORY_LABEL = {
  clothing_dresses: "Dresses",
  clothing_sets: "Sets + co-ords",
  clothing_tops: "Tops",
  clothing_outerwear: "Outerwear",
  clothing_other: "Other clothing",
  acc_jewellery: "Jewellery",
  acc_bags: "Bags",
  acc_pins: "Pins + badges",
  acc_other: "Other accessories",
  footwear: "Footwear",
  wearable_tech: "Wearable tech",
  other: "Other",
};

export default function CutApplications() {
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async (status) => {
    setLoading(true);
    try {
      const list = await api.listCutApplications(status === "all" ? undefined : status);
      setRows(list || []);
    } catch {
      toast.error("Could not load applications");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(filter); }, [filter]);

  const setStatus = async (id, newStatus) => {
    try {
      if (newStatus === "accepted") {
        // Create the Brand in the active Cut round in the same step
        try {
          const brand = await api.cutAdminAcceptApplication(id);
          toast.success(`Accepted → Brand created: ${brand.brandName}`);
          setRows((r) => r.map((x) => (x.id === id ? { ...x, status: "accepted" } : x)));
          return;
        } catch (e) {
          // If no active round or slug clash, fall back to a plain status flip
          if (e?.response?.status !== 400) throw e;
          toast.error(e.response.data.detail || "Could not create brand — check active round exists");
        }
      }
      await api.updateCutApplication(id, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status: newStatus } : x)));
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application? This can't be undone.")) return;
    try {
      await api.deleteCutApplication(id);
      setRows((r) => r.filter((x) => x.id !== id));
      if (openId === id) setOpenId(null);
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const counts = STATUSES.reduce((acc, s) => {
    acc[s.key] = s.key === "all" ? rows.length : rows.filter((r) => r.status === s.key).length;
    return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10" data-testid="cut-applications-page">
      <div className="flex items-center gap-3 mb-2">
        <Inbox size={22} className="text-[#c8a832]" />
        <h1 className="text-2xl font-semibold tracking-tight">Cut Applications</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Brands who applied at <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">/storefront/apply</code>.
        Review, accept or reject each entry.
      </p>

      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="cut-status-tabs">
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => setFilter(s.key)} data-testid={`cut-tab-${s.key}`}
            className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === s.key ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {s.label}
            {filter === s.key && counts[s.key] !== undefined && (
              <span className="text-[10px] opacity-70">{counts[s.key]}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-12 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center" data-testid="cut-empty">
          <Inbox size={28} className="mx-auto text-gray-300 mb-3" />
          <div className="text-sm font-medium text-gray-800 mb-1">No applications yet</div>
          <div className="text-xs text-gray-500">Share <code className="bg-gray-100 px-1.5 py-0.5 rounded">/storefront/apply</code> to start collecting brand entries.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <ApplicationRow key={r.id} row={r} open={openId === r.id}
              onToggle={() => setOpenId(openId === r.id ? null : r.id)}
              onStatus={(s) => setStatus(r.id, s)}
              onDelete={() => remove(r.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ApplicationRow({ row, open, onToggle, onStatus, onDelete }) {
  const s = STATUS_STYLES[row.status] || STATUS_STYLES.pending;
  const submitted = row.createdAt ? new Date(row.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
  return (
    <div className="bg-white border border-gray-200 rounded-xl" data-testid={`cut-app-row-${row.id}`}>
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-4 p-4 text-left hover:bg-gray-50 transition-colors" data-testid={`cut-app-toggle-${row.id}`}>
        <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#0a0a0a] to-[#333] text-[#c8a832] flex items-center justify-center text-lg font-bold shrink-0">
          {(row.brand_name || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold text-gray-900 truncate">{row.brand_name}</div>
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border" style={{ background: s.bg, color: s.color, borderColor: s.border }}>{row.status}</span>
          </div>
          <div className="text-xs text-gray-500 truncate mt-0.5">
            {row.product_name} · {CATEGORY_LABEL[row.category] || row.category}
          </div>
        </div>
        <div className="text-xs text-gray-400 hidden sm:block whitespace-nowrap">{submitted}</div>
      </button>

      {open && (
        <div className="border-t border-gray-100 p-5 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid={`cut-app-detail-${row.id}`}>
          <div className="md:col-span-2 space-y-4">
            <div>
              <div className="lp-section-label mb-1">Brand</div>
              <FactGrid>
                <Fact label="Brand" v={row.brand_name} />
                <Fact label="Founder" v={row.founder_name} />
                <Fact label="Email" v={<a className="text-[#c8a832] hover:underline" href={`mailto:${row.email}`}>{row.email}</a>} />
                <Fact label="Instagram" v={<a className="text-[#c8a832] hover:underline" href={`https://instagram.com/${(row.instagram || "").replace(/^@/, "")}`} target="_blank" rel="noreferrer">{row.instagram} <ExternalLink size={10} className="inline" /></a>} />
                <Fact label="Website" v={row.website ? <a className="text-[#c8a832] hover:underline" href={row.website} target="_blank" rel="noreferrer">{row.website} <ExternalLink size={10} className="inline" /></a> : "—"} />
                <Fact label="Followers" v={row.followers} />
              </FactGrid>
            </div>

            <div>
              <div className="lp-section-label mb-1">Product</div>
              <FactGrid>
                <Fact label="Product" v={row.product_name} />
                <Fact label="Category" v={CATEGORY_LABEL[row.category] || row.category} />
                <Fact label="Retail price" v={row.retail_price} />
              </FactGrid>
            </div>

            <div>
              <div className="lp-section-label mb-1">Fulfilment</div>
              <FactGrid>
                <Fact label="Online store" v={row.online_store} />
                <Fact label="Fulfilment" v={row.fulfilment} />
                <Fact label="Lead time" v={row.lead_time || "—"} />
              </FactGrid>
            </div>

            <BigText label="Why they should make the cut" text={row.brand_story} />
            {row.promo_plan && <BigText label="Promotion plan" text={row.promo_plan} />}
            {row.anything_else && <BigText label="Anything else" text={row.anything_else} />}
          </div>

          <div className="space-y-3">
            <div className="lp-section-label">Actions</div>
            <a href={`mailto:${row.email}`} className="lp-btn-secondary w-full justify-center text-xs" data-testid={`cut-app-email-${row.id}`}>
              <Mail size={13} /> Reply by email
            </a>
            {row.status !== "accepted" && (
              <button onClick={() => onStatus("accepted")} className="lp-btn-primary w-full justify-center text-xs" data-testid={`cut-app-accept-${row.id}`}>
                <Check size={13} /> Accept
              </button>
            )}
            {row.status !== "rejected" && (
              <button onClick={() => onStatus("rejected")} className="lp-btn-secondary w-full justify-center text-xs" data-testid={`cut-app-reject-${row.id}`} style={{ color: "#9b1c2a" }}>
                <X size={13} /> Reject
              </button>
            )}
            {row.status !== "pending" && (
              <button onClick={() => onStatus("pending")} className="lp-btn-secondary w-full justify-center text-xs" data-testid={`cut-app-reopen-${row.id}`}>
                <RotateCcw size={13} /> Reopen
              </button>
            )}
            <button onClick={onDelete} className="lp-btn-secondary w-full justify-center text-xs" data-testid={`cut-app-delete-${row.id}`} style={{ color: "#7a1e1e" }}>
              <Trash2 size={13} /> Delete
            </button>
            <div className="text-[10px] text-gray-400 text-center mt-2">Submitted {submitted}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const FactGrid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">{children}</div>
);
const Fact = ({ label, v }) => (
  <div className="flex flex-col">
    <span className="text-[10px] uppercase tracking-wider text-gray-400">{label}</span>
    <span className="text-gray-900 break-words">{v || "—"}</span>
  </div>
);
const BigText = ({ label, text }) => (
  <div>
    <div className="lp-section-label mb-1">{label}</div>
    <div className="text-xs text-gray-800 whitespace-pre-wrap leading-relaxed bg-gray-50 border border-gray-100 rounded-lg p-3">{text}</div>
  </div>
);
